import React, { useState, useEffect, useRef } from "react";
import {
  FormControl, MenuItem, TableRow, Table, TableHead, TableBody, TableCell,
  TableContainer, Paper, Button, Dialog, DialogActions, DialogContent,
  Typography, IconButton, Card, Box, Grid, Select, TextField, Chip,
  Collapse, InputAdornment, Tooltip, Avatar,
} from "@material-ui/core";
import TrainerService from "./Locality/Service/trainerService";
import { makeStyles } from '@material-ui/core/styles';
import TablePagination from '@material-ui/core/TablePagination';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import SkipPreviousIcon from '@material-ui/icons/SkipPrevious';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import MusicNoteIcon from '@material-ui/icons/MusicNote';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import EventIcon from '@material-ui/icons/Event';
import SchoolIcon from '@material-ui/icons/School';
import requests from './Locality/Service/httpService';

const convertToProxyUrl = (blobUrl) => {
  if (!blobUrl || typeof blobUrl !== 'string' || blobUrl.trim() === '') {
    return blobUrl;
  }
  const blobPattern = /https?:\/\/artistimages\.blob\.core\.windows\.net\/montestory\/(.+)/;
  const match = blobUrl.match(blobPattern);
  if (match && match[1]) {
    const fileName = match[1].split('?')[0];
    if (fileName) {
      const baseURL = requests.baseURL();
      return `${baseURL}/video/${encodeURIComponent(fileName)}`;
    }
  }
  return blobUrl;
};

const normalizeMediaUrls = (urls) => {
  if (Array.isArray(urls)) {
    return urls.map((u) => String(u || '').trim()).filter(Boolean);
  }
  if (urls && typeof urls === 'string' && urls.trim() !== '') {
    return [urls.trim()];
  }
  return [];
};

const getTrainerClassLabel = (trainer) => {
  if (Array.isArray(trainer.classIds) && trainer.classIds.length) {
    return trainer.classIds
      .map((c) => (typeof c === 'object' && c !== null ? c.className : c))
      .filter(Boolean)
      .join(', ');
  }
  return trainer.classId?.className || '—';
};

const getTrainerClassSearchText = (trainer) => {
  if (Array.isArray(trainer.classIds) && trainer.classIds.length) {
    return trainer.classIds
      .map((c) => (typeof c === 'object' && c !== null ? c.className : c))
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }
  return (trainer.classId?.className || '').toLowerCase();
};

const useStyles = makeStyles((theme) => ({
  pageHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  pageTitle: {
    fontWeight: 700,
    fontSize: '1.9rem',
    color: '#0f2d4d',
    lineHeight: 1.2,
  },
  pageSubtitle: {
    color: '#6b7280',
    fontSize: '0.9rem',
    marginTop: 2,
  },
  noteBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    backgroundColor: '#eef4ff',
    border: '1px solid #d6e4ff',
    marginBottom: theme.spacing(2),
  },
  noteIconWrap: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: '#1769d6',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  tableCard: {
    borderRadius: 14,
    border: '1px solid #e6eaf0',
    boxShadow: '0 2px 10px rgba(16,42,67,0.06)',
    overflow: 'hidden',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #eef1f5',
  },
  iconBtnOutlined: {
    border: '1px solid #e6eaf0',
    borderRadius: 8,
    padding: 7,
  },
  headCell: {
    backgroundColor: '#f4f6fa',
    color: '#46566b',
    fontWeight: 700,
    fontSize: '0.8rem',
    borderBottom: '1px solid #e6eaf0',
    whiteSpace: 'nowrap',
  },
  classChip: {
    backgroundColor: '#eef4ff',
    color: '#1769d6',
    fontWeight: 600,
    height: 24,
  },
  subjectChip: {
    backgroundColor: '#f3efff',
    color: '#7c4dff',
    fontWeight: 600,
    height: 24,
  },
  mediaBtn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    minWidth: 72,
    padding: '4px 12px',
    fontSize: '0.78rem',
  },
  videoBtn: {
    backgroundColor: '#1769d6',
    color: '#fff',
    '&:hover': { backgroundColor: '#125bbd' },
  },
  audioBtn: {
    backgroundColor: '#5c7a94',
    color: '#fff',
    '&:hover': { backgroundColor: '#4a6278' },
  },
  disabledMediaBtn: {
    backgroundColor: '#e0e0e0',
    color: '#9e9e9e',
  },
  profileAvatar: {
    width: 36,
    height: 36,
    objectFit: 'cover',
  },
  dialogPaper: {
    maxWidth: 900,
    width: '95%',
    borderRadius: 14,
    [theme.breakpoints.down('sm')]: {
      maxWidth: '100%',
      width: '100%',
      margin: 0,
      borderRadius: 0,
      height: '100%',
      maxHeight: '100vh',
    },
  },
  dialogTitleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 2.5),
    borderBottom: '1px solid #eef1f5',
    backgroundColor: '#fafafa',
  },
  dialogTitleText: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: '#0f2d4d',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  dialogBody: {
    padding: theme.spacing(3),
    overflow: 'hidden',
    backgroundColor: '#000',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialogFooter: {
    padding: theme.spacing(1.5, 2.5),
    borderTop: '1px solid #eef1f5',
    backgroundColor: '#fafafa',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  mediaPlayer: {
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 8,
    backgroundColor: '#000',
  },
  videoPlayer: {
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    minHeight: 400,
    maxHeight: '70vh',
    borderRadius: 8,
    backgroundColor: '#000',
    objectFit: 'contain',
    [theme.breakpoints.down('md')]: {
      minHeight: 350,
      maxHeight: '60vh',
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 250,
      maxHeight: '50vh',
    },
  },
  mediaContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
    padding: theme.spacing(2),
    width: '100%',
  },
  emptyMessage: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  emptyIcon: {
    fontSize: 64,
    color: theme.palette.text.disabled,
    marginBottom: theme.spacing(2),
  },
  navButton: {
    backgroundColor: '#5c7a94',
    color: 'white',
    '&:hover': { backgroundColor: '#4a6278' },
    '&:disabled': { backgroundColor: '#e0e0e0', color: '#9e9e9e' },
  },
  closeBtn: {
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    backgroundColor: '#1769d6',
    color: '#fff',
    padding: '8px 24px',
    '&:hover': { backgroundColor: '#125bbd' },
  },
}));

export default function TrainingSession() {
  const classes = useStyles();
  const [trainerList, setTrainerList] = useState([]);
  const [pg, setpg] = useState(0);
  const [rpg, setrpg] = useState(5);
  const [mediaUrls, setMediaUrls] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [mediaType, setMediaType] = useState("");
  const [openPlayer, setOpenPlayer] = useState(false);
  const [noteOpen, setNoteOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const videoRef = useRef(null);

  const loadTrainingSessions = () => {
    setListLoading(true);
    setLoadError("");
    TrainerService.getAllTrainer()
      .then((res) => {
        const raw = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        const list = raw
          .map((trainer) => ({
            ...trainer,
            videoURL: normalizeMediaUrls(trainer.videoURL),
            audioURL: normalizeMediaUrls(trainer.audioURL),
          }))
          .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setTrainerList(list);
      })
      .catch((err) => {
        console.error(err);
        setTrainerList([]);
        setLoadError(err?.response?.data?.message || err?.message || "Failed to load training sessions.");
      })
      .finally(() => setListLoading(false));
  };

  useEffect(() => {
    loadTrainingSessions();
    const refresh = () => loadTrainingSessions();
    window.addEventListener('trainingSessionsUpdated', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('trainingSessionsUpdated', refresh);
      window.removeEventListener('focus', refresh);
      setTrainerList([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mediaType === 'video' && mediaUrls.length > 0 && openPlayer && videoRef.current && mediaUrls[currentIndex]) {
      const video = videoRef.current;
      video.src = mediaUrls[currentIndex];
      video.load();
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {});
      }
    }
  }, [mediaUrls, currentIndex, mediaType, openPlayer]);

  const handlePlay = (urls, type) => {
    const urlArray = normalizeMediaUrls(urls);

    if (urlArray.length === 0) {
      setMediaUrls([]);
      setMediaType(type);
      setOpenPlayer(true);
      return;
    }

    const convertedUrls = urlArray.map((url) => {
      if (type === 'video') {
        return convertToProxyUrl(url);
      }
      return url;
    });

    setMediaUrls(convertedUrls);
    setCurrentIndex(0);
    setMediaType(type);
    setOpenPlayer(true);
  };

  const handleClosePlayer = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.src = '';
      videoRef.current.load();
    }
    setOpenPlayer(false);
    setMediaUrls([]);
    setCurrentIndex(0);
    setMediaType("");
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < mediaUrls.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const refreshList = () => {
    loadTrainingSessions();
  };

  const handleChangePage = (event, newpage) => {
    setpg(newpage);
  };

  const handleChangeRowsPerPage = (event) => {
    setrpg(parseInt(event.target.value, 10));
    setpg(0);
  };

  const dateAndTime = (date) => {
    const systemDate = new Date(date);
    return systemDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const hasMedia = (urls) => normalizeMediaUrls(urls).length > 0;

  const filteredList = trainerList.filter((trainer) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    const name = (trainer.trainerRegisterId?.name || '').toLowerCase();
    const className = getTrainerClassSearchText(trainer);
    const subject = (trainer.subject || '').toLowerCase();
    return name.includes(term) || className.includes(term) || subject.includes(term);
  });

  return (
    <Box p={{ xs: 1, sm: 2 }}>
      <Box className={classes.pageHeader}>
        <Box>
          <Typography className={classes.pageTitle}>Training Session</Typography>
          <Typography className={classes.pageSubtitle}>
            View trainer sessions and play training video or audio content.
          </Typography>
        </Box>
      </Box>

      {noteOpen && (
        <Box className={classes.noteBanner}>
          <Box className={classes.noteIconWrap}>
            <InfoOutlinedIcon style={{ fontSize: 18 }} />
          </Box>
          <Box flex={1}>
            <Typography style={{ fontWeight: 600, color: '#1f3a5f', fontSize: '0.9rem' }}>
              Training media sessions
            </Typography>
            <Typography style={{ color: '#46566b', fontSize: '0.85rem', marginTop: 2 }}>
              Browse trainer sessions below. Click Video or Audio to open the media player for available content.
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setNoteOpen(false)}>
            <CloseIcon style={{ fontSize: 18, color: '#7a8aa0' }} />
          </IconButton>
        </Box>
      )}

      <Card elevation={0} className={classes.tableCard}>
        <Box className={classes.toolbar}>
          <Typography style={{ fontWeight: 700, color: '#0f2d4d' }}>
            {filteredList.length} Session{filteredList.length !== 1 ? 's' : ''}
          </Typography>
          <Box display="flex" alignItems="center" gridGap={10}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search trainers..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setpg(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ fontSize: 18, color: '#9aa6b6' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Refresh">
              <IconButton className={classes.iconBtnOutlined} size="small" onClick={refreshList}>
                <RefreshIcon style={{ fontSize: 20, color: '#46566b' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {loadError && (
          <Box px={2} pb={1}>
            <Typography variant="body2" color="error">
              {loadError}
            </Typography>
          </Box>
        )}

        <TableContainer component={Paper} elevation={0} style={{ maxHeight: 520 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headCell}>Profile</TableCell>
                <TableCell className={classes.headCell}>Name</TableCell>
                <TableCell className={classes.headCell}>Class</TableCell>
                <TableCell className={classes.headCell}>Subject</TableCell>
                <TableCell className={classes.headCell}>Date</TableCell>
                <TableCell className={classes.headCell} align="center">Video</TableCell>
                <TableCell className={classes.headCell} align="center">Audio</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {listLoading && (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: 32, color: '#7a8aa0' }}>
                    Loading training sessions…
                  </TableCell>
                </TableRow>
              )}
              {!listLoading && filteredList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" style={{ padding: 32, color: '#7a8aa0' }}>
                    No training sessions found.
                  </TableCell>
                </TableRow>
              )}
              {!listLoading &&
                filteredList.slice(pg * rpg, pg * rpg + rpg).map((trainer) => (
                  <TableRow key={trainer._id} hover>
                    <TableCell>
                      {trainer.trainerRegisterId?.profile ? (
                        <Avatar
                          src={trainer.trainerRegisterId.profile}
                          alt="trainer"
                          className={classes.profileAvatar}
                        />
                      ) : (
                        <AccountCircleIcon style={{ fontSize: 36, color: '#9aa6b6' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography style={{ fontWeight: 600, color: '#0f2d4d', fontSize: '0.875rem' }}>
                        {trainer.trainerRegisterId?.name || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={<SchoolIcon style={{ fontSize: 14 }} />}
                        label={getTrainerClassLabel(trainer)}
                        className={classes.classChip}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={trainer.subject || '—'}
                        className={classes.subjectChip}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gridGap={6} style={{ color: '#46566b' }}>
                        <EventIcon style={{ fontSize: 16, color: '#9aa6b6' }} />
                        {dateAndTime(trainer.createdAt)}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {hasMedia(trainer.videoURL) ? (
                        <Button
                          variant="contained"
                          size="small"
                          className={`${classes.mediaBtn} ${classes.videoBtn}`}
                          startIcon={<VideoLibraryIcon style={{ fontSize: 16 }} />}
                          onClick={() => handlePlay(trainer.videoURL, 'video')}
                        >
                          Video
                        </Button>
                      ) : (
                        <Typography variant="caption" style={{ color: '#9aa6b6' }}>N/A</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {hasMedia(trainer.audioURL) ? (
                        <Button
                          variant="contained"
                          size="small"
                          className={`${classes.mediaBtn} ${classes.audioBtn}`}
                          startIcon={<MusicNoteIcon style={{ fontSize: 16 }} />}
                          onClick={() => handlePlay(trainer.audioURL, 'audio')}
                        >
                          Audio
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          size="small"
                          className={`${classes.mediaBtn} ${classes.disabledMediaBtn}`}
                          disabled
                        >
                          Audio
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          rowsPerPageOptions={[5, 50, 500, 10000]}
          count={filteredList.length}
          page={pg}
          onPageChange={handleChangePage}
          rowsPerPage={rpg}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>

      <Dialog
        open={openPlayer}
        onClose={handleClosePlayer}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <Box className={classes.dialogTitleBar}>
          <span className={classes.dialogTitleText}>
            {mediaType === 'audio' ? (
              <MusicNoteIcon style={{ color: '#1769d6' }} />
            ) : (
              <VideoLibraryIcon style={{ color: '#1769d6' }} />
            )}
            {mediaType === 'audio' ? 'Audio Player' : 'Video Player'}
          </span>
          <IconButton size="small" onClick={handleClosePlayer}>
            <CloseIcon style={{ fontSize: 20, color: '#7a8aa0' }} />
          </IconButton>
        </Box>

        <DialogContent className={classes.dialogBody}>
          {mediaUrls.length === 0 ? (
            <div className={classes.emptyMessage}>
              {mediaType === 'audio' ? (
                <MusicNoteIcon className={classes.emptyIcon} />
              ) : (
                <VideoLibraryIcon className={classes.emptyIcon} />
              )}
              <Typography variant="h6" style={{ marginBottom: 8, color: '#666' }}>
                No Session Available
              </Typography>
              <Typography variant="body2" style={{ color: '#999' }}>
                {mediaType === 'audio'
                  ? 'No audio files are available for this training session.'
                  : 'No video files are available for this training session.'}
              </Typography>
            </div>
          ) : (
            <div className={classes.mediaContainer}>
              {mediaType === 'audio' ? (
                <audio
                  key={mediaUrls[currentIndex]}
                  controls
                  className={classes.mediaPlayer}
                  autoPlay
                >
                  <source src={mediaUrls[currentIndex]} type="audio/mpeg" />
                  <source src={mediaUrls[currentIndex]} type="audio/mp3" />
                  <source src={mediaUrls[currentIndex]} type="audio/wav" />
                  Your browser does not support the audio tag.
                </audio>
              ) : (
                <video
                  ref={videoRef}
                  key={mediaUrls[currentIndex]}
                  controls
                  className={classes.videoPlayer}
                  autoPlay
                  preload="auto"
                  playsInline
                  controlsList="nodownload"
                  onLoadedMetadata={(e) => {
                    console.log('Video metadata loaded, duration:', e.target.duration);
                  }}
                  onCanPlay={() => {
                    console.log('Video can play');
                  }}
                  onSeeking={(e) => {
                    console.log('Seeking to:', e.target.currentTime);
                  }}
                  onSeeked={(e) => {
                    console.log('Seeked to:', e.target.currentTime);
                  }}
                  onError={(e) => {
                    const error = e.target.error;
                    if (error) {
                      console.error('Video error:', {
                        code: error.code,
                        message: error.message,
                      });
                    }
                  }}
                >
                  <source src={mediaUrls[currentIndex]} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions className={classes.dialogFooter}>
          {mediaUrls.length > 1 ? (
            <Box display="flex" alignItems="center" gridGap={12} flex={1} justifyContent="center">
              <IconButton
                className={classes.navButton}
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                aria-label="Previous"
                size="small"
              >
                <SkipPreviousIcon />
              </IconButton>
              <Typography variant="body2" style={{ color: '#666', fontWeight: 500, minWidth: 60, textAlign: 'center' }}>
                {currentIndex + 1} / {mediaUrls.length}
              </Typography>
              <IconButton
                className={classes.navButton}
                onClick={handleNext}
                disabled={currentIndex === mediaUrls.length - 1}
                aria-label="Next"
                size="small"
              >
                <SkipNextIcon />
              </IconButton>
            </Box>
          ) : (
            <Box flex={1} />
          )}
          <Button onClick={handleClosePlayer} variant="contained" className={classes.closeBtn}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
